import { useRef, useCallback, useEffect, useState } from 'react';
import { useProjectStore } from '@/store/projectStore';
import type { OverlayElement } from '@/types';
import clsx from 'clsx';
import {
  ZoomIn,
  ZoomOut,
  Magnet,
} from 'lucide-react';
import { TrimHandles, TrimControlsBar } from './TrimHandles';

// Color mapping for overlay types
const typeColors: Record<string, string> = {
  rectangle: 'bg-blue-500',
  'rounded-rectangle': 'bg-blue-400',
  circle: 'bg-purple-500',
  ellipse: 'bg-purple-400',
  polygon: 'bg-pink-500',
  text: 'bg-amber-500',
  image: 'bg-green-500',
  blur: 'bg-red-400',
};

interface TimelineTrackProps {
  overlay: OverlayElement;
  duration: number;
  pixelsPerSecond: number;
  isSelected: boolean;
  onSelect: (id: string, addToSelection?: boolean) => void;
  onUpdateTiming: (id: string, startTime: number, endTime: number) => void;
}

const TimelineTrack = ({
  overlay,
  duration,
  pixelsPerSecond,
  isSelected,
  onSelect,
  onUpdateTiming,
}: TimelineTrackProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState<'move' | 'start' | 'end' | null>(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [initialStart, setInitialStart] = useState(0);
  const [initialEnd, setInitialEnd] = useState(0);
  
  const left = overlay.startTime * pixelsPerSecond;
  const width = (overlay.endTime - overlay.startTime) * pixelsPerSecond;
  
  const handleMouseDown = useCallback((e: React.MouseEvent, type: 'move' | 'start' | 'end') => {
    if (overlay.locked) return;
    e.stopPropagation();
    
    setIsDragging(true);
    setDragType(type);
    setDragStartX(e.clientX);
    setInitialStart(overlay.startTime);
    setInitialEnd(overlay.endTime);
    
    if (!isSelected) {
      onSelect(overlay.id, e.shiftKey);
    }
  }, [overlay, isSelected, onSelect]);
  
  useEffect(() => {
    if (!isDragging || !dragType) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStartX;
      const dt = dx / pixelsPerSecond;
      
      let newStart = initialStart;
      let newEnd = initialEnd;
      
      if (dragType === 'move') {
        newStart = Math.max(0, Math.min(duration - (initialEnd - initialStart), initialStart + dt));
        newEnd = newStart + (initialEnd - initialStart);
      } else if (dragType === 'start') {
        newStart = Math.max(0, Math.min(initialEnd - 0.1, initialStart + dt));
      } else if (dragType === 'end') {
        newEnd = Math.max(initialStart + 0.1, Math.min(duration, initialEnd + dt));
      }
      
      onUpdateTiming(overlay.id, newStart, newEnd);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      setDragType(null);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragType, dragStartX, pixelsPerSecond, initialStart, initialEnd, duration, overlay.id, onUpdateTiming]);
  
  return (
    <div className="relative h-10 border-b border-editor-border/50 group hover:bg-editor-surface/30">
        <div
          className={clsx(
            'absolute top-1 bottom-1 rounded cursor-pointer transition-shadow',
            typeColors[overlay.type],
            isSelected && 'ring-2 ring-white shadow-lg',
            overlay.locked && 'opacity-50 cursor-not-allowed',
            isDragging && 'shadow-xl'
          )}
          style={{ left, width: Math.max(width, 6) }}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(overlay.id, e.shiftKey);
          }}
          onMouseDown={(e) => handleMouseDown(e, 'move')}
        >
          {/* Start resize handle */}
          <div
            className="absolute left-0 top-0 bottom-0 w-2 cursor-w-resize hover:bg-white/30 rounded-l transition-colors"
            onMouseDown={(e) => handleMouseDown(e, 'start')}
          />
          
          {/* End resize handle */}
          <div
            className="absolute right-0 top-0 bottom-0 w-2 cursor-e-resize hover:bg-white/30 rounded-r transition-colors"
            onMouseDown={(e) => handleMouseDown(e, 'end')}
          />
          
          {/* Label */}
          {width > 50 && (
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-white truncate px-3 pointer-events-none">
              {overlay.name}
            </span>
          )}
        </div>
    </div>
  );
};

export const Timeline = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  
  const video = useProjectStore((state) => state.project.video);
  const overlays = useProjectStore((state) => state.project.overlays);
  const currentTime = useProjectStore((state) => state.playerState.currentTime);
  const selectedIds = useProjectStore((state) => state.selectionState.selectedIds);
  const zoom = useProjectStore((state) => state.timelineState.zoom);
  const snapEnabled = useProjectStore((state) => state.timelineState.snapEnabled);
  
  const setCurrentTime = useProjectStore((state) => state.setCurrentTime);
  const setTimelineZoom = useProjectStore((state) => state.setTimelineZoom);
  const toggleSnap = useProjectStore((state) => state.toggleSnap);
  const selectOverlay = useProjectStore((state) => state.selectOverlay);
  const deselectAll = useProjectStore((state) => state.deselectAll);
  const updateOverlay = useProjectStore((state) => state.updateOverlay);
  
  const duration = video?.duration ?? 0;
  const pixelsPerSecond = 50 * zoom;
  // Ensure minimum width for the timeline content
  const minWidth = scrollContainerRef.current?.clientWidth ?? 800;
  const totalWidth = Math.max(duration * pixelsPerSecond + 100, minWidth);
  
  // Generate time markers
  const markers: { time: number; label: string; type: 'major' | 'minor' | 'sub' }[] = [];
  if (duration > 0) {
    const majorInterval = 5;
    const minorInterval = 1;
    const subInterval = zoom >= 2 ? 0.5 : zoom >= 1 ? 1 : zoom >= 0.5 ? 2 : 5;
    
    for (let t = 0; t <= duration; t += subInterval) {
      const roundedT = Math.round(t * 10) / 10;
      let type: 'major' | 'minor' | 'sub' = 'sub';
      
      if (roundedT % majorInterval === 0) {
        type = 'major';
      } else if (roundedT % minorInterval === 0) {
        type = 'minor';
      }
      
      markers.push({
        time: roundedT,
        label: formatTimeShort(roundedT),
        type,
      });
    }
  }
  
  function formatTimeShort(time: number): string {
    const totalSeconds = Math.floor(time);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  
  // Handle playhead drag on ruler
  const handleRulerMouseDown = useCallback((e: React.MouseEvent) => {
    if (!video || !scrollContainerRef.current) return;
    
    e.preventDefault();
    setIsDraggingPlayhead(true);
    
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const scrollLeft = scrollContainerRef.current.scrollLeft;
    const x = e.clientX - containerRect.left + scrollLeft;
    const time = Math.max(0, Math.min(duration, x / pixelsPerSecond));
    setCurrentTime(time);
  }, [video, duration, pixelsPerSecond, setCurrentTime]);
  
  useEffect(() => {
    if (!isDraggingPlayhead) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!scrollContainerRef.current) return;
      
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const x = e.clientX - containerRect.left + scrollLeft;
      const time = Math.max(0, Math.min(duration, x / pixelsPerSecond));
      setCurrentTime(time);
    };
    
    const handleMouseUp = () => {
      setIsDraggingPlayhead(false);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPlayhead, duration, pixelsPerSecond, setCurrentTime]);
  
  // Handle overlay timing update
  const handleUpdateTiming = useCallback((id: string, startTime: number, endTime: number) => {
    updateOverlay(id, { startTime, endTime });
  }, [updateOverlay]);
  
  // Handle wheel scroll for horizontal scrolling
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!scrollContainerRef.current) return;
    
    // If shift is held or it's a horizontal scroll, scroll horizontally
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      scrollContainerRef.current.scrollLeft += e.deltaX || e.deltaY;
    }
  }, []);
  
  if (!video) {
    return (
      <div className="h-52 glass-panel border-t border-white/5 flex items-center justify-center">
        <p className="text-editor-text-muted text-sm">
          Import a video to see the timeline
        </p>
      </div>
    );
  }
  
  return (
    <div className="h-52 glass-panel border-t border-white/5 flex flex-col">
      {/* Timeline controls header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-editor-border shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-editor-text-muted">Timeline</span>
          <span className="text-xs text-editor-text-dim">
            {overlays.length} overlay{overlays.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={toggleSnap}
            className={clsx(
              'btn btn-ghost btn-icon',
              snapEnabled && 'bg-editor-accent text-white'
            )}
            title={snapEnabled ? 'Disable snapping' : 'Enable snapping'}
          >
            <Magnet size={16} />
          </button>
          
          <div className="divider" />
          
          <button
            onClick={() => setTimelineZoom(Math.max(0.25, zoom - 0.25))}
            className="btn btn-ghost btn-icon"
            disabled={zoom <= 0.25}
            title="Zoom out"
          >
            <ZoomOut size={16} />
          </button>
          <span className="text-xs text-editor-text-muted w-12 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setTimelineZoom(Math.min(4, zoom + 0.25))}
            className="btn btn-ghost btn-icon"
            disabled={zoom >= 4}
            title="Zoom in"
          >
            <ZoomIn size={16} />
          </button>
        </div>
      </div>
      
      {/* Scrollable timeline content */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-x-auto overflow-y-auto timeline-scroll"
        onWheel={handleWheel}
        onClick={deselectAll}
      >
        {/* Time ruler - full width */}
        <div className="sticky top-0 z-20 bg-editor-panel">
          {/* Ruler area - starts from left edge */}
          <div
            ref={rulerRef}
            className="h-8 bg-editor-surface relative cursor-pointer border-b border-editor-border"
            style={{ width: totalWidth }}
            onMouseDown={handleRulerMouseDown}
          >
            {/* Trim handles */}
            <TrimHandles 
              duration={duration} 
              pixelsPerSecond={pixelsPerSecond}
              containerRef={rulerRef}
            />
            
            {/* Time markers */}
            {markers.map((marker, i) => (
              <div
                key={i}
                className="absolute top-0 flex flex-col items-center pointer-events-none"
                style={{ left: marker.time * pixelsPerSecond }}
              >
                <div
                  className={clsx(
                    'w-px',
                    marker.type === 'major' && 'h-4 bg-editor-accent',
                    marker.type === 'minor' && 'h-3 bg-editor-text-muted',
                    marker.type === 'sub' && 'h-2 bg-editor-border'
                  )}
                />
                {marker.type === 'major' && (
                  <span className="text-[10px] text-editor-text font-medium mt-0.5 bg-editor-accent/20 px-1 rounded">
                    {marker.label}
                  </span>
                )}
                {marker.type === 'minor' && zoom >= 0.75 && (
                  <span className="text-[9px] text-editor-text-dim mt-0.5">
                    {marker.label}
                  </span>
                )}
              </div>
            ))}
            
            {/* Playhead on ruler */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-30 pointer-events-none"
              style={{ left: currentTime * pixelsPerSecond }}
            >
              <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-red-500 rounded-sm transform rotate-45" />
            </div>
          </div>
        </div>
        
        {/* Tracks container */}
        <div className="relative" style={{ minWidth: totalWidth }}>
          {/* Playhead line through tracks */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-red-500/60 z-10 pointer-events-none"
            style={{ left: currentTime * pixelsPerSecond }}
          />
          
          {/* Overlay tracks */}
          {overlays.map((overlay) => (
            <TimelineTrack
              key={overlay.id}
              overlay={overlay}
              duration={duration}
              pixelsPerSecond={pixelsPerSecond}
              isSelected={selectedIds.includes(overlay.id)}
              onSelect={selectOverlay}
              onUpdateTiming={handleUpdateTiming}
            />
          ))}
          
          {/* Empty state */}
          {overlays.length === 0 && (
            <div className="flex items-center justify-center h-24 text-editor-text-muted text-sm">
              Add overlays using the toolbar above
            </div>
          )}
        </div>
      </div>
      
      {/* Trim Controls Bar */}
      <TrimControlsBar />
    </div>
  );
};
